import { useState, useRef } from 'react';
import { MRIContainer, MRILabel, MRIBody, MRITag, MRITagRemove, MRIInput } from './MultiResponseInput.styles';

/**
 * A text input that lets the user build an array of string values.
 * Press Enter or comma to add the current draft as a new tag.
 * Each tag has an × to remove it.
 *
 * Props:
 *   label      – floating label text
 *   pH         – placeholder shown in the text input
 *   values     – string[]  (controlled)
 *   onChange   – (newValues: string[]) => void
 *   id         – optional HTML id for the input
 */
export default function MultiResponseInput({ label, pH = '', values = [], onChange, id }) {
    const [draft, setDraft] = useState('');
    const inputRef = useRef(null);

    function addDraft(raw) {
        const trimmed = raw.trim();
        if (!trimmed || values.includes(trimmed)) return;
        onChange([...values, trimmed]);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addDraft(draft);
            setDraft('');
        } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
            onChange(values.slice(0, -1));
        }
    }

    function handleBlur() {
        if (draft.trim()) {
            addDraft(draft);
            setDraft('');
        }
    }

    function removeValue(val) {
        onChange(values.filter((v) => v !== val));
    }

    return (
        <MRIContainer onClick={() => inputRef.current?.focus()}>
            <MRILabel htmlFor={id}>{label || pH}</MRILabel>
            <MRIBody>
                {values.map((val) => (
                    <MRITag key={val}>
                        {val}
                        <MRITagRemove
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeValue(val); }}
                            tabIndex={-1}
                        >
                            ×
                        </MRITagRemove>
                    </MRITag>
                ))}
                <MRIInput
                    ref={inputRef}
                    id={id}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={values.length === 0 ? pH : ''}
                />
            </MRIBody>
        </MRIContainer>
    );
}
